import { mutationOptions, queryOptions } from '@tanstack/react-query';

import { PortalApiClient } from '../../../rest-api/portalApiClient';
import { projectQuizesQueryKey } from '../../../rest-api/queries/sharedQueries';
import type { CourseExportLink } from '../rest-api/courseExportLinksApi';
import { tagKeys, type TagTile } from '../../project/components/ProjectsCore.queries';

export const MAX_LINKS_PER_EXPORT = 3;

export type CourseExportDetail = Awaited<ReturnType<PortalApiClient['getCourseExportDetail']>>;

/**
 * Reports a failed link mutation to the user. Passed in rather than reached for here so these
 * options stay free of React hooks and remain usable from tests and stories.
 */
export type NotifyLinkError = (title: string, description: string) => void;

export const courseExportDetailKeys = {
  all: ['courseExportDetail'] as const,
  detail: (courseExportGuid: string) => [...courseExportDetailKeys.all, courseExportGuid] as const,
} as const;

export const getCourseExportDetailQueryOptions = (
  portalApiClient: Pick<PortalApiClient, 'getCourseExportDetail'>,
  courseExportGuid: string
) =>
  queryOptions({
    queryKey: courseExportDetailKeys.detail(courseExportGuid),
    queryFn: async () => await portalApiClient.getCourseExportDetail(courseExportGuid),
    // Processing finishes in a background job nothing notifies the page about, so the list polls
    // until every version has either been processed or failed.
    refetchInterval: (query) =>
      query.state.data?.versions.some((v) => v.processedAtTimestamp === null && v.processingFailedAtTimestamp === null)
        ? 5000
        : false,
  });

export const getCreateLinkMutationOptions = (
  portalApiClient: Pick<PortalApiClient, 'createCourseExportLink'>,
  courseExportGuid: string,
  notifyError: NotifyLinkError
) =>
  mutationOptions({
    meta: { errorToast: false },
    mutationFn: async () => await portalApiClient.createCourseExportLink(courseExportGuid),
    // Nothing is rendered optimistically for a create, so without this a refused create looks
    // like the button did nothing at all.
    onError: () => {
      notifyError('Could not create the link', 'The link was not created. Please try again.');
    },
    onSettled: (_data, _error, _variables, _onMutateResult, context) => {
      // The server generates the slug and the absolute URL, so there is nothing to render
      // optimistically -- the row appears when the refetch lands.
      void context.client.invalidateQueries({ queryKey: courseExportDetailKeys.detail(courseExportGuid) });
    },
  });

export const getSetLinkActiveMutationOptions = (
  portalApiClient: Pick<PortalApiClient, 'setCourseExportLinkActive'>,
  courseExportGuid: string,
  notifyError: NotifyLinkError
) =>
  mutationOptions({
    meta: { errorToast: false },
    mutationFn: portalApiClient.setCourseExportLinkActive,
    // The checkbox must move under the user's finger, so the cache is written before the request.
    onMutate: (variables, context) => {
      const key = courseExportDetailKeys.detail(courseExportGuid);
      const previousDetail = context.client.getQueryData<CourseExportDetail>(key);
      if (!previousDetail) {
        return { previousDetail };
      }

      context.client.setQueryData(key, {
        ...previousDetail,
        links: previousDetail.links.map((link: CourseExportLink) =>
          link.guid === variables.linkGuid ? { ...link, isActive: variables.isActive } : link
        ),
      });
      return { previousDetail };
    },
    onError: (_error, variables, onMutateResult, context) => {
      // The checkbox has already moved optimistically, so it is rolled back and the user is told
      // why it snapped back.
      if (onMutateResult?.previousDetail) {
        context.client.setQueryData(courseExportDetailKeys.detail(courseExportGuid), onMutateResult.previousDetail);
      }
      notifyError(
        variables.isActive ? 'Could not activate the link' : 'Could not deactivate the link',
        'The link is unchanged. Please try again.'
      );
    },
    onSettled: (_data, _error, _variables, _onMutateResult, context) => {
      void context.client.invalidateQueries({ queryKey: courseExportDetailKeys.detail(courseExportGuid) });
    },
  });

export const getDeleteLinkMutationOptions = (
  portalApiClient: Pick<PortalApiClient, 'deleteCourseExportLink'>,
  courseExportGuid: string,
  notifyError: NotifyLinkError
) =>
  mutationOptions({
    meta: { errorToast: false },
    mutationFn: portalApiClient.deleteCourseExportLink,
    onMutate: (linkGuid, context) => {
      const key = courseExportDetailKeys.detail(courseExportGuid);
      const previousDetail = context.client.getQueryData<CourseExportDetail>(key);
      if (!previousDetail) {
        return { previousDetail };
      }

      context.client.setQueryData(key, {
        ...previousDetail,
        links: previousDetail.links.filter((link: CourseExportLink) => link.guid !== linkGuid),
      });
      return { previousDetail };
    },
    onError: (_error, _variables, onMutateResult, context) => {
      // The row was removed optimistically; put it back and say so.
      if (onMutateResult?.previousDetail) {
        context.client.setQueryData(courseExportDetailKeys.detail(courseExportGuid), onMutateResult.previousDetail);
      }
      notifyError('Could not delete the link', 'The link is still active. Please try again.');
    },
    onSettled: (_data, _error, _variables, _onMutateResult, context) => {
      void context.client.invalidateQueries({ queryKey: courseExportDetailKeys.detail(courseExportGuid) });
    },
  });

export const getSetTagToCourseExportMutationOptions = (
  portalApiClient: Pick<PortalApiClient, 'setTagToCourseExport'>,
  courseExportGuid: string,
  notifyError: NotifyLinkError
) =>
  mutationOptions({
    meta: { errorToast: false },
    mutationFn: portalApiClient.setTagToCourseExport,
    // The chip has to appear the moment it is clicked, so the detail cache is written before the
    // request goes out, mirroring how the Distribution list handles the same mutation.
    onMutate: (variables, context) => {
      const key = courseExportDetailKeys.detail(courseExportGuid);
      const previousDetail = context.client.getQueryData<CourseExportDetail>(key);
      if (!previousDetail) {
        return { previousDetail };
      }

      const tag = context.client
        .getQueryData<TagTile[]>(tagKeys.lists())
        ?.find((candidate) => candidate.guid === variables.tagGuid);

      if (variables.assign && !tag) {
        // Nothing to render a chip from; let the refetch fill it in.
        return { previousDetail };
      }

      const withoutTag = previousDetail.tags.filter((existing) => existing.guid !== variables.tagGuid);
      // The server returns tags alphabetically, so the optimistic list matches and no chip jumps
      // position once the refetch lands.
      const tags =
        variables.assign && tag
          ? [...withoutTag, { guid: tag.guid, name: tag.name, color: tag.color }].sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
            )
          : withoutTag;

      context.client.setQueryData(key, { ...previousDetail, tags });
      return { previousDetail };
    },
    onError: (_error, _variables, onMutateResult, context) => {
      if (onMutateResult?.previousDetail) {
        context.client.setQueryData(courseExportDetailKeys.detail(courseExportGuid), onMutateResult.previousDetail);
      }
      notifyError('Tag not updated', 'The tag could not be changed. Please try again.');
    },
    onSettled: (_data, _error, _variables, _onMutateResult, context) => {
      void context.client.invalidateQueries({ queryKey: courseExportDetailKeys.detail(courseExportGuid) });
    },
  });

/**
 * For a dialog that creates a version and reports its own failure, so the app-wide fallback toast
 * stays quiet. `always` fails at once when offline instead of pausing with the submit button
 * spinning until the connection is back.
 */
const createVersionMutationDefaults = {
  meta: { errorToast: false },
  networkMode: 'always',
} as const;

export const getAddVersionMutationOptions = (
  portalApiClient: Pick<PortalApiClient, 'addCourseExportVersion'>,
  courseExportGuid: string,
  notifyError: NotifyLinkError
) =>
  mutationOptions({
    ...createVersionMutationDefaults,
    mutationFn: portalApiClient.addCourseExportVersion,
    onError: () => notifyError('Version not created', 'The version could not be created. Please try again.'),
    onSettled: (_data, _error, _variables, _onMutateResult, context) => {
      void context.client.invalidateQueries({ queryKey: courseExportDetailKeys.detail(courseExportGuid) });
    },
  });

export const getAddVersionFromProjectMutationOptions = (
  portalApiClient: Pick<PortalApiClient, 'addCourseExportVersionFromProject'>,
  courseExportGuid: string,
  notifyError: NotifyLinkError
) =>
  mutationOptions({
    ...createVersionMutationDefaults,
    mutationFn: portalApiClient.addCourseExportVersionFromProject,
    onError: () => notifyError('Version not created', 'The version could not be created. Please try again.'),
    onSettled: (_data, _error, variables, _onMutateResult, context) => {
      void context.client.invalidateQueries({ queryKey: courseExportDetailKeys.detail(courseExportGuid) });
      // The export saves the chosen tracked items on the project.
      void context.client.invalidateQueries({ queryKey: projectQuizesQueryKey(variables.projectId) });
    },
  });

export const getVersionPreviewUrlQueryOptions = (
  portalApiClient: Pick<PortalApiClient, 'getCourseExportVersionPreviewUrl'>,
  courseExportGuid: string,
  versionGuid: string
) =>
  queryOptions({
    // Not under courseExportDetailKeys: invalidating a module's detail must not refetch this and
    // swap the iframe's src mid-preview.
    queryKey: ['courseExportVersionPreviewUrl', courseExportGuid, versionGuid] as const,
    queryFn: async () => await portalApiClient.getCourseExportVersionPreviewUrl({ courseExportGuid, versionGuid }),
    // A version's package never changes once processed, so neither does the URL it is served at.
    staleTime: Infinity,
  });

export const getPublishVersionMutationOptions = (
  portalApiClient: Pick<PortalApiClient, 'publishExportVersion'>,
  courseExportGuid: string,
  notifyError: NotifyLinkError
) =>
  mutationOptions({
    meta: { errorToast: false },
    mutationFn: (versionGuid: string) => portalApiClient.publishExportVersion({ courseExportGuid, versionGuid }),
    onError: () => notifyError('Version not swapped', 'The current version is unchanged. Please try again.'),
    onSettled: (_data, _error, _variables, _onMutateResult, context) => {
      void context.client.invalidateQueries({ queryKey: courseExportDetailKeys.detail(courseExportGuid) });
    },
  });

export const getDeleteVersionMutationOptions = (
  portalApiClient: Pick<PortalApiClient, 'deleteCourseExportVersion'>,
  courseExportGuid: string,
  notifyError: NotifyLinkError
) =>
  mutationOptions({
    meta: { errorToast: false },
    mutationFn: (versionGuid: string) => portalApiClient.deleteCourseExportVersion({ courseExportGuid, versionGuid }),
    onError: () => notifyError('Version not deleted', 'The version could not be deleted. Please try again.'),
    onSettled: (_data, _error, _variables, _onMutateResult, context) => {
      void context.client.invalidateQueries({ queryKey: courseExportDetailKeys.detail(courseExportGuid) });
    },
  });

export {
  getListTagsQueryOptions,
  getUpdateTagMutationOptions,
  getDeleteTagMutationOptions,
  type TagTile,
} from '../../project/components/ProjectsCore.queries';

export type CreateCourseExportTagParams = Parameters<PortalApiClient['createTag']>[0] & {
  /**
   * Assigns the new tag to this module as part of creating it. A tag created from the module's own
   * tag dropdown is meant for that module, so it is assigned straight away — the same as creating
   * one from a course tile on the Courses page.
   */
  courseExportGuidToAssign?: string;
};

/**
 * Creates a tag and, when asked, assigns it to a module in the same step. The courses page has its
 * own version of this that assigns to a project instead; the two differ only in what they assign to.
 */
export const getCreateTagMutationOptions = (
  portalApiClient: Pick<PortalApiClient, 'createTag' | 'setTagToCourseExport'>
) =>
  mutationOptions({
    mutationFn: async (variables: CreateCourseExportTagParams) => {
      const tag = await portalApiClient.createTag(variables);
      if (!tag.guid) {
        throw new Error('Created tag is missing guid');
      }

      if (variables.courseExportGuidToAssign) {
        await portalApiClient.setTagToCourseExport({
          courseExportGuid: variables.courseExportGuidToAssign,
          tagGuid: tag.guid,
          assign: true,
        });
      }

      return { ...tag, guid: tag.guid };
    },
    onSettled: (_data, _error, variables, _onMutateResult, context) => {
      // The new tag has to appear in the filter and row dropdowns straight away.
      void context.client.invalidateQueries({ queryKey: tagKeys.lists() });
      if (variables.courseExportGuidToAssign) {
        void context.client.invalidateQueries({
          queryKey: courseExportDetailKeys.detail(variables.courseExportGuidToAssign),
        });
      }
    },
  });
